import React, { useState } from 'react'
import { t, Trans } from '@lingui/macro'

import { ManageTheme } from './ManageTheme'
import Loader from '../../../components/common/loader/Loader'
import { ResourceList } from '../resource-list/ResourceList'
import IconShare from '../../../components/common/icon/IconShare'
import { CreateThemeModal } from '../create-theme-modal/CreateThemeModal'
import CreateResourceModal from '../create-resource-modal/CreateResourceModal'
import DeleteResourceModal from '../delete-resource-modal/DeleteResourceModal'
import ResourceAssignmentModal from '../resource-assignment-modal/ResourceAssignmentModal'
import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { IResourceListItemCallbackArgs } from '../resource-list/ResourceListItem'
import { ManageTopic } from 'types/Topic'
import {
  ButtonClean,
  ButtonIcon,
  ButtonLight,
  ButtonPrimary,
  Flex
} from 'front-end/src/components'
import {
  useReorderTopicsInWorkspaceMutation,
  useWorkspaceManageTopicsAndThemesQuery
} from './workspaceManageTopicsAndThemes.codegen'
import { useModal } from 'front-end/src/app/hooks/useModal'
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc'
import IconDrag from '../../../components/common/icon/IconDrag'
import { useWorkspaceRearrangeThemes } from 'front-end/src/pages/manage-workspace-page/resources-subpage/useWorkspaceRearrangeThemes'
import { Box } from '@chakra-ui/react'
import EmptyResourcesSvg from 'public/assets/img/empty-resources.svg'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'

type Props = { match: { params: { workspaceId: string } } }

// TODO find out what the hell is that TODO below about
// TODO rewrite this whole component using GQL and react context, when this is done uncomment refetch in CopyResourceModal
const ResourcesSubpage: React.FC<Props> = (props) => {
  const { open, currentModal } = useModal()
  const { userIsAdminOrManager } = usePublicApiSession()
  const [isAssigning, setAssigning] = useState(false)
  const [resourcesToAssign, setResourcesToAssign] = useState<
    IResourceListItemCallbackArgs[]
  >([])

  const clearAssign = () => {
    setAssigning(false)
    setResourcesToAssign([])
  }

  const workspaceId = +props.match.params.workspaceId

  const { data, refetch, loading } = useWorkspaceManageTopicsAndThemesQuery({
    // I think this was wrong:
    // fetch-policy: 'cache-first',
    fetchPolicy: 'network-only',
    variables: { workspaceId }
  })

  const topicToResourceListItemMapper = React.useCallback(
    (topic: ManageTopic) => {
      return {
        ...topic,

        viewsCount: 1,
        numberRated: topic.number_rated,
        rating: topic.number_rated
          ? topic.total_rating / topic.number_rated
          : null,

        onEdit: () => {
          navigateTo(linkBuilder.topicEditUrl(topic.id))
        },
        onDelete: ({ id }: IResourceListItemCallbackArgs) => {
          open(DeleteResourceModal, { resourceId: id }).then(() => refetch())
        },

        onSelect: ({
          id,
          draftVersion,
          currentTopicVersion: publishedVersion
        }: IResourceListItemCallbackArgs) => {
          if (!resourcesToAssign.find((r) => r.id === id)) {
            const resource = {
              id,
              draftVersion,
              currentTopicVersion: publishedVersion
            }
            setResourcesToAssign([...resourcesToAssign, resource])
          }
        },
        onDeselect: ({ id }: IResourceListItemCallbackArgs) =>
          setResourcesToAssign((rs) => rs.filter((r) => r.id !== id)),
        isSelected:
          isAssigning && resourcesToAssign.find((r) => r.id === topic.id)
      }
    },
    [isAssigning, resourcesToAssign]
  )

  const {
    themes,
    handleSortEnd,
    loading: isRearrangeInProgress
  } = useWorkspaceRearrangeThemes(data, workspaceId, refetch)

  const [reorderTopicsInWorkspace] = useReorderTopicsInWorkspaceMutation()

  const wrappedReorderResourcesInGql = React.useCallback(
    async (topicIds: number[]) => {
      await reorderTopicsInWorkspace({
        variables: {
          topicIds,
          workspaceId
        }
      })
    },
    [reorderTopicsInWorkspace]
  )

  const workspaceTopics = React.useMemo(
    () => data?.workspace?.topics.map(topicToResourceListItemMapper),
    [data?.workspace?.topics, topicToResourceListItemMapper]
  )

  if (!data || loading) {
    return <Loader />
  }

  const { workspace } = data
  if (!workspace) {
    return null
  }

  const isCoordinator = workspace?.__typename === 'WorkspaceModelAsCoordinator'
  const isCollaborator =
    workspace?.__typename === 'WorkspaceModelAsCollaborator'

  // TODO: create some useACL or something
  const canCreateResources =
    userIsAdminOrManager || isCoordinator || isCollaborator
  const canShareResources = userIsAdminOrManager || isCoordinator

  return (
    <>
      <header className="ManageWorkspacePage__resources-header">
        {!isAssigning && (
          <div className="ManageWorkspacePage__actions">
            {canShareResources && (
              <ButtonLight
                onClick={() => setAssigning(true)}
                leftIcon={<IconShare />}
              >
                <Trans>Share</Trans>
              </ButtonLight>
            )}
            {canCreateResources && (
              <ButtonPrimary
                ml="4px"
                onClick={() =>
                  open(CreateResourceModal, {
                    workspaceId,
                    series: workspace.themes
                  }).then((resource) =>
                    navigateTo(linkBuilder.topicEditUrl(resource.id))
                  )
                }
              >
                <Trans>Create a resource</Trans>
              </ButtonPrimary>
            )}
          </div>
        )}

        {isAssigning && (
          <Flex className="ManageWorkspacePage__share">
            <Flex grow={1} className="resources-message">
              <Trans>Select one or more resources to share:</Trans>
            </Flex>

            <ButtonClean onClick={clearAssign}>
              <Trans>Cancel</Trans>
            </ButtonClean>
            <ButtonPrimary
              disabled={resourcesToAssign.length === 0}
              onClick={() =>
                open(ResourceAssignmentModal, {
                  workspaceId,
                  resourcesToAssign: resourcesToAssign
                }).finally(clearAssign)
              }
              leftIcon={<IconShare />}
            >
              <Trans>Share {resourcesToAssign.slice().length} resources</Trans>
            </ButtonPrimary>
          </Flex>
        )}
      </header>

      {workspaceTopics && (
        <ResourceList
          resources={workspaceTopics}
          workspaceId={workspaceId}
          showSelection={isAssigning}
          refetchTopics={refetch}
          rearrangeResources={wrappedReorderResourcesInGql}
        />
      )}

      {!workspace?.topics && (
        <div className="no-items">
          <h2>
            <Trans>No resources yet?</Trans>
          </h2>
          <p>
            <Trans>
              Start creating a learning resource by clicking <br />
              on the ‘Create a resource' button above
            </Trans>
          </p>
        </div>
      )}

      <SortableDiv
        lockAxis="y"
        useDragHandle
        onSortEnd={handleSortEnd}
        helperClass="dragging"
      >
        {themes?.map((theme, idx: number) => {
          return (
            <ManageThemeElement
              key={theme.id}
              theme={theme}
              index={idx as number}
              DragHandle={DragHandle}
              refetchWorkspace={refetch}
              isRearrangeInProgress={isRearrangeInProgress}
              topicToResourceListItemMapper={topicToResourceListItemMapper}
              isAssigning={isAssigning}
            />
          )
        })}
      </SortableDiv>

      {
        <ButtonLight
          onClick={() =>
            open(CreateThemeModal, { workspaceId }).then(() => refetch())
          }
        >
          <Trans>Create a theme</Trans>
        </ButtonLight>
      }

      {currentModal}

      {workspace.topics.length === 0 && workspace.themes.length === 0 && (
        <div className="no-items">
          <img src={EmptyResourcesSvg} alt={t`Empty resource`} />
          <h2>
            <Trans>Create a resource</Trans>
          </h2>
          <p>
            <Trans>
              Start creating a learning resource by clicking <br />
              on the ‘Create a resource' button above
            </Trans>
          </p>
        </div>
      )}
    </>
  )
}

const SortableDiv = SortableContainer((props) => <Box {...props} />)
const ManageThemeElement = SortableElement(ManageTheme)
const DragHandle = SortableHandle(() => (
  <ButtonIcon
    icon={<IconDrag position="relative" top="1px" />}
    cursor="row-resize"
    hint={t`Drag`}
    aria-label={t`Drag`}
    circular
  />
))

export default ResourcesSubpage
